// Modo "Carrusel" — pantalla propia (ruta /carrusel).
//
// Promueve a modo la función que hasta ahora sólo existía como un botón adentro
// del historial (GeneratedImagesHistory) y del visor inline (GeneratedImagesInline),
// donde no la encontraba nadie que no abriera una imagen generada. Suma además un
// caso nuevo: partir de una FOTO PROPIA, no sólo de lo que generó Atlas — para eso
// el backend aprendió a aceptar una referencia de storage además del id de fila
// (ver ADR-2026-08-14-modo-carrusel).
//
// UI: calcada de AnimarImagenMode, que es el otro modo que arranca de una imagen:
//   • desktop → panel fijo a la derecha de la sidebar (bg gris + esquinas
//     redondeadas), header con "Volver", y DOS columnas: carátula + texto a la
//     izquierda, card blanca de opciones a la derecha.
//   • mobile  → carátula full-bleed arriba y panel blanco redondeado abajo
//     (-mt-6 rounded-t-3xl), con el back flotante del shell (mobileBackOverride,
//     que además esconde la bottom nav).
//
// Dos pantallas: (1) elegir la imagen — subir foto o elegir una generada;
// (2) elegir cuántas variaciones y generar. Al generar: pantalla de carga estándar
// → la galería de resultados, igual que el botón de siempre. La foto subida NO se
// registra en `imagenes_generadas` (ver lib/animarImagenUpload).
//
// OJO con los colores: las clases token-con-opacidad (`text-brand-black/90`,
// `bg-brand-primary/10`) NO compilan en este Tailwind — quedan sin regla en el CSS.
// Por eso acá se usa `text-brand-black` pelado y hex arbitrario con alpha
// (`text-[#3C3C43]/60`, `bg-[#1644EB]/10`), que sí compilan.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ImageUp,
  Images,
  Info,
  Loader2,
  Minus,
  Plus,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useSidebar } from '../../../context/SidebarContext';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { CreditBadge } from '../../../components/CreditBadge';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  ANGLES_SUBIDA_REQUIERE_ATLAS,
  postGenerateAnglesFrom,
  type AnglesSource,
} from '../../../lib/generateAngles';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';

/** Carátula del modo — PLACEHOLDER: la misma ilustración que usa el menú viejo
 *  (CarruselMenu). Todavía no hay un clip propio del modo. */
const HERO_IMG = '/images/carrusel.png';

/** Copy del hero, espeja el tono de los otros modos. */
const HERO_DESC =
  'Convertí una foto en varias: Atlas genera nuevas tomas del mismo producto en la misma escena, cambiando ángulo, pose y encuadre.';

/** Cuántas variaciones pedir. El tope es 6 porque es lo que clampea el backend
 *  (`Math.min(6, …)` en el handler de nuevos ángulos): ofrecer más sería prometer
 *  algo que se recorta en silencio. El mínimo es 2 — un carrusel de una no es un
 *  carrusel, igual que en CarruselMenu. */
const CANTIDAD_MIN = 2;
const CANTIDAD_MAX = 6;

/** Estimación para la barra de la pantalla de carga: el backend espera hasta 200s
 *  (prompt del carrusel ~25s + las N generaciones en paralelo). */
const CARGA_MS = 180_000;

/** Cuántas veces se pollean las filas nuevas antes de mandar a la galería igual. */
const POLL_INTENTOS = 20;
const POLL_INTERVALO_MS = 1500;

const GENERADAS_BUCKET = 'generadas';
const SIGNED_TTL = 3600;

interface PreloadedImage {
  id: string;
  fileName: string;
  signedUrl: string;
  favorito: boolean;
}

export default function CarruselMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // ── Pantalla 1: elegir la imagen ──
  const [paso, setPaso] = useState<'elegir' | 'configurar'>('elegir');
  const [source, setSource] = useState<AnglesSource | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetGeneradas, setSheetGeneradas] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando.
  const uploadSeqRef = useRef(0);

  // ── Pantalla 2: cuántas variaciones ──
  const [cantidad, setCantidad] = useState(CANTIDAD_MIN);
  const [generando, setGenerando] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{
    imageIds: string[];
    companyId: string;
    preloaded: PreloadedImage[];
  } | null>(null);

  // 1 crédito por imagen nueva (el backend cobra 1 por hijo generado).
  const costo = cantidad;

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      setUserId(auth.user.id);
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      setCompanyId(
        (p as { id_company?: string | null } | null)?.id_company ?? null,
      );
    })();
  }, []);

  const volverAElegir = () => {
    setPaso('elegir');
    setSource(null);
    setPreviewUrl(null);
    setCantidad(CANTIDAD_MIN);
    setError(null);
  };

  // Back flotante del shell en mobile. Además de dar el botón, esconde la bottom
  // nav (MobileBottomNav se apaga con `mobileBackOverride !== null`), que es lo
  // que hacen el resto de los modos al ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'configurar') volverAElegir();
      else navigate('/modos');
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso]);

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setError(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId, 'carrusel');
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      setSource({ tipo: 'subida', bucket: subida.bucket, path: subida.path });
      setPreviewUrl(subida.previewUrl);
      setPaso('configurar');
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      if (seq === uploadSeqRef.current) setSubiendo(false);
    }
  };

  const elegirGenerada = (pick: GeneradaPick) => {
    setSource({ tipo: 'generada', imageId: pick.id });
    setPreviewUrl(pick.signedUrl);
    setSheetGeneradas(false);
    setPaso('configurar');
  };

  /** Espera a que las filas nuevas tengan `file_name` y precarga sus URLs firmadas,
   *  para que la galería de resultados no muestre huecos. Mismo criterio que el
   *  botón de carrusel del historial. */
  const precargarNuevas = async (ids: string[], company: string) => {
    const preloaded: PreloadedImage[] = [];
    for (let intento = 1; intento <= POLL_INTENTOS; intento++) {
      for (const imgId of ids) {
        if (preloaded.some((p) => p.id === imgId)) continue;
        try {
          const { data: fila } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imgId)
            .eq('id_company', company)
            .single();
          if (!fila?.file_name) continue;
          const { data: signed } = await supabase.storage
            .from(GENERADAS_BUCKET)
            .createSignedUrl(`${company}/full/${fila.file_name}`, SIGNED_TTL);
          if (!signed?.signedUrl) continue;
          preloaded.push({
            id: imgId,
            fileName: fila.file_name,
            signedUrl: signed.signedUrl,
            favorito: fila.favorito === true,
          });
          const img = new Image();
          img.src = signed.signedUrl; // deja la imagen en el cache del browser
        } catch {
          // fila todavía no visible: se reintenta en la próxima vuelta
        }
      }
      if (preloaded.length === ids.length) break;
      await new Promise((r) => setTimeout(r, POLL_INTERVALO_MS));
    }
    return preloaded;
  };

  const generar = async () => {
    if (!source || !userId || !companyId || generando) return;
    setGenerando(true);
    setError(null);
    setShowLoading(true);
    setCompleteNow(false);
    try {
      // Guard con el dato fresco de Supabase. El carrusel NO tiene restricción de
      // plan (paridad con el botón del historial): sólo créditos suficientes para
      // la cantidad pedida.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', companyId)
        .single();
      const restantes = (perfil as { imagenes_restantes?: number | null } | null)
        ?.imagenes_restantes;
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        const plan = (perfil as { plan?: string } | null)?.plan;
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      const ids = await postGenerateAnglesFrom(source, {
        userId,
        cantidad,
      });
      if (ids.length === 0) {
        setShowLoading(false);
        setGenerando(false);
        setError('No se pudo generar el carrusel. Intentá nuevamente.');
        return;
      }

      const preloaded = await precargarNuevas(ids, companyId);
      pendingNavRef.current = { imageIds: ids, companyId, preloaded };
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[CarruselMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar el carrusel. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas entre mobile y desktop ────────────────────────────

  const heroImg = (className: string) => (
    <img
      src={HERO_IMG}
      alt="Ejemplo del modo Carrusel"
      className={className}
    />
  );

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  /** Encabezado + CTAs del aporte de imagen (mismo bloque que "Elegí el producto
   *  para generar" de los modos con producto). */
  const bloqueElegirImagen = (variant: 'mobile' | 'desktop') => (
    <>
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#1644EB]/10 flex items-center justify-center flex-shrink-0">
          <Images className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black leading-tight">
            Elegí la imagen del carrusel
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            Atlas va a generar nuevas tomas del mismo producto, en la misma escena.
          </p>
        </div>
      </div>

      {/* Con el flag de rollback a n8n no hay forma de partir de una foto suelta:
          en ese caso queda sólo el camino de las generadas. */}
      {!ANGLES_SUBIDA_REQUIERE_ATLAS && (
        <button
          type="button"
          onClick={elegirArchivo}
          disabled={subiendo}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-60 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          {subiendo ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <ImageUp className="w-5 h-5" /> Subir imagen
            </>
          )}
        </button>
      )}

      {/* MOBILE: la grilla vive en un sheet, así el panel de abajo no se llena.
          DESKTOP: la grilla va inline debajo del separador. */}
      {variant === 'mobile' ? (
        <button
          type="button"
          onClick={() => setSheetGeneradas(true)}
          className="w-full mt-2.5 inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary text-brand-black text-[15.5px] font-semibold transition-colors"
        >
          <Images className="w-5 h-5" /> Elegir de mis generadas
        </button>
      ) : (
        <div className="flex items-center gap-3 mt-5 mb-1">
          <div className="flex-1 h-px bg-black/[0.06]" />
          <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
            o elegí una que ya generaste
          </span>
          <div className="flex-1 h-px bg-black/[0.06]" />
        </div>
      )}
      {errorBox}
    </>
  );

  /** Opciones del carrusel (pantalla 2). Igual en las dos variantes. */
  const bloqueConfigurar = (
    <>
      <p className="text-[15px] text-brand-black leading-snug mb-5">
        Se generarán{' '}
        <span className="font-semibold">{cantidad} imágenes nuevas</span> a partir de
        esta foto, con distintos ángulos y variaciones.
      </p>

      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Cantidad
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-[15px] text-brand-black">Imágenes a generar</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCantidad((n) => Math.max(CANTIDAD_MIN, n - 1))}
                disabled={cantidad <= CANTIDAD_MIN}
                aria-label="Menos"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-[16px] font-semibold text-brand-black w-5 text-center tabular-nums">
                {cantidad}
              </span>
              <button
                type="button"
                onClick={() => setCantidad((n) => Math.min(CANTIDAD_MAX, n + 1))}
                disabled={cantidad >= CANTIDAD_MAX}
                aria-label="Más"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">
              Se consumirán {costo} imágenes
            </span>
          </div>
        </div>
      </div>
      {errorBox}
    </>
  );

  /** Botón Generar — sticky al fondo del panel de opciones, igual que los modos. */
  const botonGenerar = (
    <div className="shrink-0 bg-white px-5 pt-3 pb-5 border-t border-black/[0.06]">
      <button
        type="button"
        onClick={generar}
        disabled={generando}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        <Sparkles className="w-5 h-5" />
        Generar carrusel
        <CreditBadge credits={costo} tone="on-primary" size="sm" />
      </button>
    </div>
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        <GeneradasPickGrid
          companyId={companyId}
          selectedId={null}
          onSelect={elegirGenerada}
          emptyHint="Todavía no generaste ninguna imagen. Subí una foto para armar el carrusel."
        />
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando carrusel…"
          subMessage="Esto puede tardar unos minutos."
          durationMs={CARGA_MS}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: {
                  imageIds: pend.imageIds,
                  companyId: pend.companyId,
                  preloadedImages: pend.preloaded,
                },
              });
            }
          }}
        />
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  const enConfigurar = paso === 'configurar' && !!source && !!previewUrl;

  return (
    <>
      {/* ── MOBILE: carátula full-bleed + panel blanco redondeado abajo ────── */}
      <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
        <div className="relative flex-1 min-h-0 bg-neutral-100">
          {enConfigurar ? (
            <CachedImg
              src={previewUrl as string}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            heroImg(
              'absolute inset-0 w-full h-full object-contain p-6',
            )
          )}
        </div>
        <div
          className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
          style={{ maxHeight: enConfigurar ? '70dvh' : '55dvh' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
            {enConfigurar ? bloqueConfigurar : bloqueElegirImagen('mobile')}
          </div>
          {enConfigurar && botonGenerar}
        </div>
      </div>

      {/* ── DESKTOP: panel a la derecha de la sidebar, dos columnas ────────── */}
      <div
        className="hidden md:flex fixed inset-y-0 right-0 z-30 bg-surface-content rounded-tl-[26px] rounded-bl-[26px] flex-col"
        style={{ left: sidebarWidth }}
      >
        <header className="shrink-0 px-6 pt-5 pb-3">
          <button
            type="button"
            onClick={() => (enConfigurar ? volverAElegir() : navigate('/modos'))}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
        </header>

        <div className="px-8 pb-6 flex flex-row gap-6 justify-center flex-1 min-h-0 items-stretch">
          {/* Columna izquierda: carátula del modo (o la imagen elegida) + copy. */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3 min-w-0">
            {enConfigurar ? (
              // `w-fit` + `relative`: el wrapper se ajusta a la caja REAL de la
              // imagen, así el badge queda pegado a su esquina y no a la de la
              // columna (que es mucho más ancha). Por eso el tope de alto va en
              // `vh` y no en `%`: un porcentaje se resolvería contra el wrapper,
              // que tiene altura automática, y no limitaría nada. El panel es
              // `fixed inset-y-0`, así que el viewport es la referencia correcta.
              <div className="relative w-fit">
                <CachedImg
                  src={previewUrl as string}
                  alt=""
                  className="max-w-[480px] max-h-[70vh] w-auto h-auto object-contain rounded-2xl"
                />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm py-1.5 px-3 text-[12.5px] font-medium text-white shadow-sm">
                  <Check className="w-3.5 h-3.5" />
                  Imagen seleccionada
                </span>
              </div>
            ) : (
              <>
                {heroImg(
                  'max-w-[420px] max-h-[60%] w-auto h-auto object-contain rounded-2xl',
                )}
                <p className="text-[12.5px] text-[#3C3C43]/60 leading-relaxed text-center text-balance max-w-[360px]">
                  {HERO_DESC}
                </p>
              </>
            )}
          </div>

          {/* Columna derecha: card blanca de opciones. */}
          <aside className="shrink-0 flex-1 max-w-[560px] flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-6">
              {enConfigurar ? (
                bloqueConfigurar
              ) : (
                <>
                  {bloqueElegirImagen('desktop')}
                  <div className="mt-3">
                    <GeneradasPickGrid
                      companyId={companyId}
                      selectedId={null}
                      onSelect={elegirGenerada}
                      emptyHint="Todavía no generaste ninguna imagen. Subí una foto para armar el carrusel."
                    />
                  </div>
                </>
              )}
            </div>
            {enConfigurar && botonGenerar}
          </aside>
        </div>
      </div>

      {overlays}
    </>
  );
}
